import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Filter, GitCompare, BookOpen, Phone } from "lucide-react";
import { fetchPrinters, fetchBlog } from "@/lib/api";
import PrinterCard from "@/components/PrinterCard";
import StarRating from "@/components/StarRating";
import { TYPE_LABELS, CONTACT } from "@/lib/constants";

const CATEGORY_CARDS = [
    { type: "inkjet", label: "Inkjet", copy: "Best for photo and mixed-content jobs." },
    { type: "laser", label: "Laser", copy: "Fast, cheap per-page, unfussed by time." },
    { type: "all-in-one", label: "All-in-One", copy: "Print, scan, copy, fax — one machine." },
    { type: "3d", label: "3D", copy: "The desktop 3D printers worth buying." },
];

export default function Home() {
    const [featured, setFeatured] = useState([]);
    const [latest, setLatest] = useState([]);
    const [articles, setArticles] = useState([]);

    useEffect(() => {
        fetchPrinters({ featured: true }).then(setFeatured).catch(() => {});
        fetchPrinters().then(all => setLatest(all.slice(0, 6))).catch(() => {});
        fetchBlog({ limit: 3 }).then(setArticles).catch(() => {});
    }, []);

    const hero = featured[0];

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative border-b border-zinc-800 overflow-hidden grain">
                <div className="container-tight pt-16 pb-16 md:pt-24 md:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
                    <div className="lg:col-span-7 animate-fade-up">
                        <div className="inline-flex items-center gap-2 border border-zinc-800 bg-zinc-950/60 px-3 py-1.5 rounded-sm">
                            <Sparkles className="h-3.5 w-3.5 text-yellow-400" />
                            <span className="text-[11px] tracking-[0.22em] uppercase text-zinc-400">Independent Reviews · Since 2025</span>
                        </div>
                        <h1 className="mt-6 font-display font-bold text-4xl sm:text-5xl lg:text-7xl tracking-tighter text-white leading-[0.95]">
                            The printer<br />
                            <span className="text-zinc-500">market, </span>
                            <span className="text-blue-500">honestly </span>
                            reviewed.
                        </h1>
                        <p className="mt-8 text-base md:text-lg text-zinc-400 max-w-xl leading-relaxed">
                            Editorial reviews of the printers people actually buy — inkjet, laser, all-in-one, and 3D.
                            Side-by-side spec comparisons, cost-per-page math, and pros/cons written by an independent editorial team.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-3">
                            <Link to="/reviews" className="btn-primary" data-testid="hero-cta-reviews">
                                Browse reviews <ArrowRight className="h-4 w-4" />
                            </Link>
                            <Link to="/compare" className="btn-ghost" data-testid="hero-cta-compare">
                                <GitCompare className="h-4 w-4" /> Compare specs
                            </Link>
                        </div>
                        <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
                            <div>
                                <div className="font-display text-3xl md:text-4xl text-white font-bold tracking-tight">53</div>
                                <div className="text-[10px] tracking-[0.22em] uppercase text-zinc-500 mt-1">Reviewed</div>
                            </div>
                            <div>
                                <div className="font-display text-3xl md:text-4xl text-white font-bold tracking-tight">28</div>
                                <div className="text-[10px] tracking-[0.22em] uppercase text-zinc-500 mt-1">Brands</div>
                            </div>
                            <div>
                                <div className="font-display text-3xl md:text-4xl text-white font-bold tracking-tight">48</div>
                                <div className="text-[10px] tracking-[0.22em] uppercase text-zinc-500 mt-1">FAQs</div>
                            </div>
                        </div>
                    </div>

                    {hero && (
                        <div className="lg:col-span-5">
                            <Link
                                to={`/reviews/${hero.slug}`}
                                data-testid="hero-featured-card"
                                className="group block card-flat overflow-hidden hover:border-zinc-600 transition-colors duration-200"
                            >
                                <div className="relative aspect-[5/4] overflow-hidden bg-zinc-900">
                                    <img src={hero.image} alt={hero.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                    <div className="absolute top-4 left-4 bg-blue-600 text-white text-[10px] tracking-[0.22em] uppercase px-2.5 py-1.5 font-medium">
                                        Editor&apos;s Pick
                                    </div>
                                </div>
                                <div className="p-6">
                                    <div className="eyebrow">{hero.brand} · {TYPE_LABELS[hero.type]}</div>
                                    <h3 className="font-display text-2xl font-semibold tracking-tight text-white mt-2 group-hover:text-blue-400 transition-colors">{hero.name}</h3>
                                    <p className="text-sm text-zinc-400 mt-3 leading-relaxed line-clamp-2">{hero.tagline}</p>
                                    <div className="mt-5 pt-5 border-t border-zinc-900 flex items-center justify-between">
                                        <StarRating value={hero.rating} />
                                        <div className="font-mono text-sm text-white">${hero.price_usd.toFixed(0)}</div>
                                    </div>
                                </div>
                            </Link>
                        </div>
                    )}
                </div>
            </section>

            {/* CATEGORIES */}
            <section className="container-tight py-20 md:py-24">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <div className="eyebrow">01 · Categories</div>
                        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-white mt-3">Find your kind of printer</h2>
                    </div>
                    <Link to="/reviews" className="hidden md:inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white transition-colors" data-testid="all-categories-link">
                        View all reviews <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800">
                    {CATEGORY_CARDS.map((cat, i) => (
                        <Link
                            key={cat.type}
                            to={`/reviews?type=${cat.type}`}
                            data-testid={`category-${cat.type}`}
                            className="group bg-zinc-950 p-8 hover:bg-zinc-900 transition-colors duration-200"
                        >
                            <div className="font-mono text-xs text-zinc-500">0{i + 1}</div>
                            <div className="font-display text-2xl font-semibold tracking-tight text-white mt-4 group-hover:text-blue-400 transition-colors">{cat.label}</div>
                            <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{cat.copy}</p>
                            <div className="mt-6 text-xs tracking-[0.2em] uppercase text-zinc-500 group-hover:text-blue-400 inline-flex items-center gap-1 transition-colors">
                                Explore <ArrowRight className="h-3 w-3" />
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* FEATURED REVIEWS */}
            <section className="container-tight py-20 md:py-24 border-t border-zinc-800">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <div className="eyebrow">02 · Editor&apos;s Picks</div>
                        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-white mt-3">Featured reviews</h2>
                    </div>
                    <Link to="/reviews" className="hidden md:inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white transition-colors">
                        All reviews <ArrowRight className="h-4 w-4" />
                    </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {featured.slice(0, 3).map(p => (
                        <PrinterCard key={p.id} printer={p} priority />
                    ))}
                </div>
            </section>

            {/* COMPARE PROMO */}
            <section className="container-tight py-20 md:py-24 border-t border-zinc-800">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7">
                        <div className="eyebrow">03 · Tool</div>
                        <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight text-white mt-3 leading-[1.05]">
                            Side-by-side spec comparison.<br />
                            <span className="text-zinc-500">No marketing gloss.</span>
                        </h2>
                        <p className="text-zinc-400 mt-6 max-w-lg leading-relaxed">
                            Pick up to three printers. Compare speed, resolution, connectivity, duty cycle,
                            and per-page cost in one strict, honest table.
                        </p>
                        <Link to="/compare" className="btn-primary mt-8" data-testid="compare-section-cta">
                            Open comparison tool <GitCompare className="h-4 w-4" />
                        </Link>
                    </div>
                    <div className="lg:col-span-5 card-flat p-6 md:p-8">
                        <div className="font-mono text-xs text-zinc-500 tracking-widest">SAMPLE.COMPARE</div>
                        <table className="w-full mt-4 text-sm">
                            <thead>
                                <tr className="text-left text-zinc-500 border-b border-zinc-800">
                                    <th className="py-3 font-normal text-xs tracking-widest uppercase">Spec</th>
                                    <th className="py-3 font-normal text-xs tracking-widest uppercase text-blue-400">HP</th>
                                    <th className="py-3 font-normal text-xs tracking-widest uppercase text-yellow-400">Brother</th>
                                </tr>
                            </thead>
                            <tbody className="text-zinc-300">
                                <tr className="border-b border-zinc-900"><td className="py-3">Speed (mono)</td><td>22 ppm</td><td>32 ppm</td></tr>
                                <tr className="border-b border-zinc-900"><td className="py-3">Duplex</td><td>Auto</td><td>Auto</td></tr>
                                <tr className="border-b border-zinc-900"><td className="py-3">Resolution</td><td>4800 x 1200</td><td>2400 x 600</td></tr>
                                <tr><td className="py-3">Price</td><td className="font-mono text-white">$249</td><td className="font-mono text-white">$179</td></tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* LATEST REVIEWS */}
            <section className="container-tight py-20 md:py-24 border-t border-zinc-800">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <div className="eyebrow">04 · Latest</div>
                        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-white mt-3">Recently reviewed</h2>
                    </div>
                    <Link to="/reviews" className="btn-ghost text-sm hidden md:inline-flex">
                        <Filter className="h-4 w-4" /> Browse all
                    </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {latest.map(p => <PrinterCard key={p.id} printer={p} />)}
                </div>
            </section>

            {/* BLOG */}
            <section className="container-tight py-20 md:py-24 border-t border-zinc-800">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <div className="eyebrow">05 · Long reads</div>
                        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-white mt-3">From the blog</h2>
                    </div>
                    <Link to="/blog" className="hidden md:inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white transition-colors">
                        <BookOpen className="h-4 w-4" /> All articles
                    </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {articles.map(a => (
                        <Link
                            key={a.id}
                            to={`/blog/${a.slug}`}
                            data-testid={`article-card-${a.slug}`}
                            className="group card-flat overflow-hidden hover:border-zinc-600 transition-colors duration-200"
                        >
                            <div className="aspect-video overflow-hidden bg-zinc-900">
                                <img src={a.image} alt={a.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                            </div>
                            <div className="p-6">
                                <div className="flex items-center gap-3 text-[10px] tracking-[0.2em] uppercase text-zinc-500">
                                    <span className="text-blue-400">{a.category}</span>
                                    <span>·</span>
                                    <span>{a.read_minutes} min read</span>
                                </div>
                                <h3 className="font-display text-lg md:text-xl font-semibold tracking-tight text-white mt-3 leading-snug group-hover:text-blue-400 transition-colors">
                                    {a.title}
                                </h3>
                                <p className="text-sm text-zinc-400 mt-3 leading-relaxed line-clamp-2">{a.excerpt}</p>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* TALK TO AN EXPERT CTA */}
            <section className="border-t border-zinc-800 bg-zinc-950/60" data-testid="expert-cta-section">
                <div className="container-tight py-20 md:py-28">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-7">
                            <div className="eyebrow">Need a recommendation?</div>
                            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tighter text-white mt-3 leading-[1.05]">
                                Talk to a printer expert.<br />
                                <span className="text-zinc-500">Free consultation, honest advice.</span>
                            </h2>
                            <p className="text-zinc-400 mt-6 max-w-lg leading-relaxed">
                                Not sure which printer fits your workflow? Tell us your volume, budget, and use case —
                                we&apos;ll shortlist the two or three machines we&apos;d actually buy in your situation.
                            </p>
                            <div className="mt-8 flex flex-wrap items-center gap-3">
                                <a href={CONTACT.phoneHref} className="btn-primary" data-testid="home-phone-cta">
                                    <Phone className="h-4 w-4" /> Call {CONTACT.phoneDisplay}
                                </a>
                                <Link to="/contact" className="btn-ghost" data-testid="home-contact-cta">
                                    Send a message <ArrowRight className="h-4 w-4" />
                                </Link>
                            </div>
                            <div className="mt-6 text-xs text-zinc-500 tracking-wide">
                                {CONTACT.hours} · No obligation · No spam
                            </div>
                        </div>
                        <div className="lg:col-span-5">
                            <a
                                href={CONTACT.phoneHref}
                                data-testid="home-phone-hero-card"
                                className="group card-flat p-8 md:p-10 block hover:border-blue-500/60 transition-colors duration-200"
                            >
                                <div className="h-12 w-12 rounded-sm bg-blue-600/15 border border-blue-600/30 flex items-center justify-center">
                                    <Phone className="h-6 w-6 text-blue-400" />
                                </div>
                                <div className="eyebrow mt-6">Toll free · US &amp; Canada</div>
                                <div className="font-display text-4xl md:text-5xl font-bold text-white mt-3 tracking-tighter group-hover:text-blue-400 transition-colors" data-testid="home-phone-display">
                                    {CONTACT.phoneDisplay}
                                </div>
                                <div className="mt-6 pt-6 border-t border-zinc-900 text-xs text-zinc-500 leading-relaxed">
                                    Speak with a real person — no phone tree, no upsell. Just printer advice.
                                </div>
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
